const STORAGE_KEY_WHITE = "home-auto-remove-white-bg";
const STORAGE_KEY_BLACK = "home-auto-remove-black-bg";

export type RemoveWhiteBgOptions = {
  /** RGB channel minimum treated as white (0–255). */
  threshold?: number;
  /** Feather semi-white edge pixels over this many levels below threshold. */
  softRange?: number;
};

export type RemoveBlackBgOptions = {
  /** RGB channel maximum treated as black (0–255). */
  threshold?: number;
  /** Feather near-black edge pixels over this many levels above threshold. */
  softRange?: number;
};

export type ApplyBackgroundRemovalOptions = {
  removeWhite?: boolean;
  removeBlack?: boolean;
  white?: RemoveWhiteBgOptions;
  black?: RemoveBlackBgOptions;
};

export function isAutoRemoveWhiteBgEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_WHITE) === "1";
  } catch {
    return false;
  }
}

export function isAutoRemoveBlackBgEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_BLACK) === "1";
  } catch {
    return false;
  }
}

export function setAutoRemoveWhiteBgEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY_WHITE, enabled ? "1" : "0");
  } catch {
    /* ignore quota / private mode */
  }
}

export function setAutoRemoveBlackBgEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY_BLACK, enabled ? "1" : "0");
  } catch {
    /* ignore quota / private mode */
  }
}

/** Single UI toggle: both white and black removal follow this preference. */
export function isAutoRemoveBgEnabled(): boolean {
  return isAutoRemoveWhiteBgEnabled() || isAutoRemoveBlackBgEnabled();
}

export function setAutoRemoveBgEnabled(enabled: boolean): void {
  setAutoRemoveWhiteBgEnabled(enabled);
  setAutoRemoveBlackBgEnabled(enabled);
}

export function homeAutoRemoveBgToggleMarkup(options: {
  id: string;
  extraClass?: string;
  compact?: boolean;
}): string {
  const classes = [
    "home-bg-auto-toggle",
    options.compact ? "home-bg-auto-toggle--compact" : "",
    options.extraClass ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return `
    <label class="${classes}" title="ตัดพื้นขาวและดำเมื่ออัปโหลด">
      <input type="checkbox" id="${options.id}" />
      <span class="home-bg-auto-toggle__gem" aria-hidden="true"></span>
      <span class="home-bg-auto-toggle__copy">
        <span class="home-bg-auto-toggle__title">ลบพื้นหลังอัตโนมัติ</span>
        <span class="home-bg-auto-toggle__hint">ขาวและดำ</span>
      </span>
    </label>`;
}

export function bindAutoRemoveBgCheckbox(input: HTMLInputElement): void {
  input.dataset.autoRemoveBg = "true";
  input.checked = isAutoRemoveBgEnabled();
  input.addEventListener("change", () => {
    const enabled = input.checked;
    setAutoRemoveBgEnabled(enabled);
    document
      .querySelectorAll<HTMLInputElement>('input[data-auto-remove-bg="true"]')
      .forEach((el) => {
        if (el !== input) el.checked = enabled;
      });
    window.dispatchEvent(
      new CustomEvent("home-remove-white-bg-changed", { detail: { enabled } }),
    );
    window.dispatchEvent(
      new CustomEvent("home-remove-black-bg-changed", { detail: { enabled } }),
    );
  });
}

export function bindAutoRemoveWhiteBgCheckbox(input: HTMLInputElement): void {
  input.dataset.autoRemoveWhiteBg = "true";
  input.checked = isAutoRemoveWhiteBgEnabled();
  input.addEventListener("change", () => {
    setAutoRemoveWhiteBgEnabled(input.checked);
    document
      .querySelectorAll<HTMLInputElement>('input[data-auto-remove-white-bg="true"]')
      .forEach((el) => {
        if (el !== input) el.checked = input.checked;
      });
    window.dispatchEvent(
      new CustomEvent("home-remove-white-bg-changed", {
        detail: { enabled: input.checked },
      }),
    );
  });
}

export function bindAutoRemoveBlackBgCheckbox(input: HTMLInputElement): void {
  input.dataset.autoRemoveBlackBg = "true";
  input.checked = isAutoRemoveBlackBgEnabled();
  input.addEventListener("change", () => {
    setAutoRemoveBlackBgEnabled(input.checked);
    document
      .querySelectorAll<HTMLInputElement>('input[data-auto-remove-black-bg="true"]')
      .forEach((el) => {
        if (el !== input) el.checked = input.checked;
      });
    window.dispatchEvent(
      new CustomEvent("home-remove-black-bg-changed", {
        detail: { enabled: input.checked },
      }),
    );
  });
}

export async function applyAutoBackgroundRemovalFromDataUrl(dataUrl: string): Promise<string> {
  return applyBackgroundRemovalFromDataUrl(dataUrl, {
    removeWhite: isAutoRemoveWhiteBgEnabled(),
    removeBlack: isAutoRemoveBlackBgEnabled(),
  });
}

export function removeWhiteFromImageData(
  imageData: ImageData,
  options?: RemoveWhiteBgOptions,
): void {
  const threshold = options?.threshold ?? 248;
  const softRange = options?.softRange ?? 18;
  const softStart = threshold - softRange;
  const px = imageData.data;

  for (let i = 0; i < px.length; i += 4) {
    const r = px[i]!;
    const g = px[i + 1]!;
    const b = px[i + 2]!;
    const a = px[i + 3]!;

    if (r >= threshold && g >= threshold && b >= threshold) {
      px[i + 3] = 0;
      continue;
    }

    const min = Math.min(r, g, b);
    if (min >= softStart && a > 0) {
      const t = (min - softStart) / softRange;
      px[i + 3] = Math.round(a * (1 - Math.min(1, t)));
    }
  }
}

export function removeBlackFromImageData(
  imageData: ImageData,
  options?: RemoveBlackBgOptions,
): void {
  const threshold = options?.threshold ?? 20;
  const softRange = options?.softRange ?? 24;
  const softEnd = threshold + softRange;
  const px = imageData.data;

  for (let i = 0; i < px.length; i += 4) {
    const r = px[i]!;
    const g = px[i + 1]!;
    const b = px[i + 2]!;
    const a = px[i + 3]!;
    const max = Math.max(r, g, b);

    if (max <= threshold) {
      px[i + 3] = 0;
      continue;
    }

    if (max <= softEnd && a > 0) {
      const t = (softEnd - max) / softRange;
      px[i + 3] = Math.round(a * (1 - Math.min(1, t)));
    }
  }
}

export function applyRemoveWhiteBackgroundToCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  options?: RemoveWhiteBgOptions,
): void {
  const imageData = ctx.getImageData(0, 0, width, height);
  removeWhiteFromImageData(imageData, options);
  ctx.putImageData(imageData, 0, 0);
}

export async function removeWhiteBackgroundFromDataUrl(
  dataUrl: string,
  options?: RemoveWhiteBgOptions,
): Promise<string> {
  return applyBackgroundRemovalFromDataUrl(dataUrl, { removeWhite: true, white: options });
}

export async function removeBlackBackgroundFromDataUrl(
  dataUrl: string,
  options?: RemoveBlackBgOptions,
): Promise<string> {
  return applyBackgroundRemovalFromDataUrl(dataUrl, { removeBlack: true, black: options });
}

export async function applyBackgroundRemovalFromDataUrl(
  dataUrl: string,
  options: ApplyBackgroundRemovalOptions,
): Promise<string> {
  if (!options.removeWhite && !options.removeBlack) return dataUrl;

  const img = await loadImageElement(dataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  if (options.removeWhite) {
    removeWhiteFromImageData(imageData, options.white);
  }
  if (options.removeBlack) {
    removeBlackFromImageData(imageData, options.black);
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL("image/png");
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
    img.src = src;
  });
}
